import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

/**
 * Renders an image as a grid of pixel particles on a canvas. Moving the pointer
 * over the canvas pushes nearby pixels away from their home positions; a spring
 * pulls them back so they ease into place (z3lka/cool-pixelation-effect style).
 *
 * Gracefully falls back to a plain CSS background image if the source image
 * taints the canvas (CORS) or fails to load. Respects prefers-reduced-motion by
 * rendering a static pixel grid without pointer displacement.
 */
const PixelImage = ({
  src,
  className = '',
  pixelSize = 6,
  radius = 60,
  force = 0.1,
}) => {
  const wrapperRef = useRef(null)
  const canvasRef = useRef(null)

  useEffect(() => {
    const wrapper = wrapperRef.current
    const canvas = canvasRef.current
    if (!wrapper || !canvas) return

    // Skip pixel effect on mobile/small screens — fall back to plain image.
    const isMobile = window.matchMedia('(max-width: 768px)').matches
    if (isMobile) {
      canvas.style.display = 'none'
      wrapper.style.backgroundImage = `url(${src})`
      wrapper.style.backgroundSize = 'cover'
      wrapper.style.backgroundPosition = 'center'
      return
    }

    const ctx = canvas.getContext('2d')
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    let particles = []
    let rafId = null
    let imgLoaded = false
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    // Mouse in canvas (device) pixels; start far away so nothing is displaced.
    // vx/vy track the cursor's per-move velocity so pixels are dragged along its path.
    const mouse = { x: -9999, y: -9999, vx: 0, vy: 0 }

    const image = new Image()
    image.crossOrigin = 'anonymous'

    // --- Fallback: render as a plain background image ---
    const fallback = () => {
      cancelAnimationFrame(rafId)
      canvas.style.display = 'none'
      wrapper.style.backgroundImage = `url(${src})`
      wrapper.style.backgroundSize = 'cover'
      wrapper.style.backgroundPosition = 'center'
    }

    // --- Build particles by cover-fitting the image, then sampling pixels ---
    const buildParticles = () => {
      const cw = wrapper.clientWidth
      const ch = wrapper.clientHeight
      if (!cw || !ch || !imgLoaded) return

      canvas.width = Math.floor(cw * dpr)
      canvas.height = Math.floor(ch * dpr)

      const W = canvas.width
      const H = canvas.height

      // cover-fit: scale so the image fills the canvas, centered
      const scale = Math.max(W / image.width, H / image.height)
      const dw = image.width * scale
      const dh = image.height * scale
      const dx = (W - dw) / 2
      const dy = (H - dh) / 2

      ctx.clearRect(0, 0, W, H)
      ctx.drawImage(image, dx, dy, dw, dh)

      let data
      try {
        data = ctx.getImageData(0, 0, W, H).data
      } catch (e) {
        // Tainted canvas (CORS) — fall back to a background image.
        fallback()
        return
      }

      const step = Math.max(2, Math.floor(pixelSize * dpr))
      const next = []
      for (let y = 0; y < H; y += step) {
        for (let x = 0; x < W; x += step) {
          const i = (y * W + x) * 4
          const a = data[i + 3]
          if (a < 16) continue // skip transparent pixels
          next.push({
            homeX: x,
            homeY: y,
            x,
            y,
            vx: 0,
            vy: 0,
            color: `rgba(${data[i]},${data[i + 1]},${data[i + 2]},${a / 255})`,
          })
        }
      }
      particles = next
      drawStatic(step)
    }

    // Draw particles at their current positions.
    const drawStatic = (step) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      for (let p = 0; p < particles.length; p++) {
        const pt = particles[p]
        ctx.fillStyle = pt.color
        ctx.fillRect(pt.x, pt.y, step, step)
      }
    }

    const animate = () => {
      const step = Math.max(2, Math.floor(pixelSize * dpr))
      const r = radius * dpr
      const r2 = r * r
      // Cursor speed drives both the drag and the scatter spread.
      const speed = Math.hypot(mouse.vx, mouse.vy)
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      for (let p = 0; p < particles.length; p++) {
        const pt = particles[p]
        const dxm = pt.x - mouse.x
        const dym = pt.y - mouse.y
        const dist2 = dxm * dxm + dym * dym

        if (dist2 < r2 && speed > 0.1) {
          const proximity = (r - Math.sqrt(dist2)) / r // 0..1, strongest at center
          // Drag the pixel along the cursor's direction of travel...
          pt.vx += mouse.vx * proximity * force
          pt.vy += mouse.vy * proximity * force
          // ...plus a little random scatter so they spread along the path.
          pt.vx += (Math.random() - 0.5) * proximity * speed
          pt.vy += (Math.random() - 0.5) * proximity * speed
        }

        // spring back home + friction
        pt.vx += (pt.homeX - pt.x) * 0.06
        pt.vy += (pt.homeY - pt.y) * 0.06
        pt.vx *= 0.82
        pt.vy *= 0.82
        pt.x += pt.vx
        pt.y += pt.vy

        ctx.fillStyle = pt.color
        ctx.fillRect(pt.x, pt.y, step, step)
      }

      // Decay cursor velocity so a stationary pointer exerts no force.
      mouse.vx *= 0.9
      mouse.vy *= 0.9

      rafId = requestAnimationFrame(animate)
    }

    // --- Pointer handling ---
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect()
      const nx = (e.clientX - rect.left) * dpr
      const ny = (e.clientY - rect.top) * dpr
      if (mouse.x > -9000) {
        mouse.vx = nx - mouse.x
        mouse.vy = ny - mouse.y
      }
      mouse.x = nx
      mouse.y = ny
    }
    const onLeave = () => {
      mouse.x = -9999
      mouse.y = -9999
      mouse.vx = 0
      mouse.vy = 0
    }

    image.onload = () => {
      imgLoaded = true
      buildParticles()
      if (canvas.style.display === 'none') return // fell back during build
      if (!reducedMotion) {
        canvas.addEventListener('mousemove', onMove)
        canvas.addEventListener('mouseleave', onLeave)
        rafId = requestAnimationFrame(animate)
      }
    }
    image.onerror = fallback
    image.src = src

    // Rebuild on resize (debounced via rAF).
    let resizeScheduled = false
    const ro = new ResizeObserver(() => {
      if (resizeScheduled) return
      resizeScheduled = true
      requestAnimationFrame(() => {
        resizeScheduled = false
        if (canvas.style.display === 'none') return
        cancelAnimationFrame(rafId)
        buildParticles()
        if (canvas.style.display !== 'none' && !reducedMotion) {
          rafId = requestAnimationFrame(animate)
        }
      })
    })
    ro.observe(wrapper)

    return () => {
      cancelAnimationFrame(rafId)
      ro.disconnect()
      canvas.removeEventListener('mousemove', onMove)
      canvas.removeEventListener('mouseleave', onLeave)
      image.onload = null
      image.onerror = null
    }
  }, [src, pixelSize, radius, force])

  return (
    <div ref={wrapperRef} className={className}>
      <canvas ref={canvasRef} />
    </div>
  )
}

PixelImage.propTypes = {
  src: PropTypes.string.isRequired,
  className: PropTypes.string,
  pixelSize: PropTypes.number,
  radius: PropTypes.number,
  force: PropTypes.number,
}

export default PixelImage
